import { Box, Paper, Stack, Typography } from '@mui/material';
import { type GridApiPro, useGridApiRef } from '@mui/x-data-grid-pro';
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';

import { GetCustomerStatusCountsResponse } from '@vy/api-definition';
import { CUSTOMER_STATUSES, CustomerStatus } from '@vy/types';
import { isNullOrUndefined } from '@vy/utils';
import { VyLayout } from '../../../components/new-design/layout/VyLayout';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useCustomersCountByStatus } from '../../../hooks/requests/use-customers-count-by-status';
import { useCustomersOverviewsFromParamsNew } from '../../../hooks/requests/use-customers-overviews/use-customers-overviews-from-params-new';
import { usePageFiltersPersistence } from '../../../hooks/use-page-filters-persistence';
import { usePaginationResetHandler } from '../../../hooks/use-pagination-reset-handler';
import { usePersistedDataGridScroll } from '../../../hooks/use-persisted-data-grid-scroll';
import { CustomersStatusCounterChart } from './customers-page-components/customers-status-counter-chart/CustomersStatusCounterChart';
import { CustomersTable } from './customers-page-components/CustomersTable';
import { useCustomersFilters } from './customers-page-hooks/use-customers-filters';
import { getStatusDisplayLabel } from './utils/getStatusDisplayLabel';
import { CUSTOMERS_QUERY_SEARCH_PARAM_KEY } from './customers-page-hooks/use-query-input-param-consts';

const CUSTOMERS_PERSISTED_FILTER_PARAMS = ['customerFilters', CUSTOMERS_QUERY_SEARCH_PARAM_KEY] as const;

const colorMap: Record<CustomerStatus, string> = {
  [CUSTOMER_STATUSES.Active]: '#87DE9C',
  [CUSTOMER_STATUSES.Inactive]: '#D6DBE7',
  [CUSTOMER_STATUSES.Suspended]: '#FD99AA',
  [CUSTOMER_STATUSES.Trial]: '#9C80FF',
};

const CustomersPage: React.FC = () => {
  const apiRef = useGridApiRef();
  const filters = useCustomersFilters();

  const {
    data,
    isFetching: isFetchingCustomers,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
  } = useCustomersOverviewsFromParamsNew();

  const { data: customerStatusCounts } = useCustomersCountByStatus();

  usePersistedDataGridScroll(apiRef as React.MutableRefObject<GridApiPro>, 'customers-grid', data?.items.length ?? 0, {
    mode: 'session',
    debounceMs: 200,
    restoreWhen: 'onRows',
  });

  usePaginationResetHandler(data?.items.length ?? 0, isFetchingCustomers);

  usePageFiltersPersistence(CUSTOMERS_PERSISTED_FILTER_PARAMS);

  const customerStatuses = useMemo(() => {
    if (isNullOrUndefined(customerStatusCounts)) return [];

    return createStatusCounterChartData(customerStatusCounts);
  }, [customerStatusCounts]);

  return (
    <>
      <Helmet>
        <title>Customers</title>
      </Helmet>
      <VyLayout
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Stack
          direction="row"
          sx={{
            gap: "140px",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: 4
          }}>
          <Typography
            sx={{
              fontSize: 24,
              padding: 1.5
            }}>
            Customers
          </Typography>

          <Box sx={{
            flexGrow: 1
          }}>
            <CustomersStatusCounterChart data={customerStatuses} />
          </Box>
        </Stack>

        <Paper
          elevation={1}
          sx={{
            height: '100%',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <CustomersTable
            apiRef={apiRef as React.MutableRefObject<GridApiPro>}
            filters={filters}
            items={data?.items ?? []}
            itemsCount={data?.count ?? 0}
            loadingItems={isFetchingCustomers}
            page={page}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </Paper>
      </VyLayout>
    </>
  );
};

function createStatusCounterChartData(statusCounts: GetCustomerStatusCountsResponse) {
  return statusCounts.map(({ status, count }) => ({
    label: getStatusDisplayLabel(status),
    value: count,
    color: colorMap[status],
  }));
}

const CustomersPageWithSuspenseBoundary = withSuspenseBoundary(withGlobalSearchParams(CustomersPage));

export { CustomersPageWithSuspenseBoundary as CustomersPage };
